import React, { useMemo } from "react";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { FundParsePositionMappingRow, FundParsePositionMappingSummary } from "./fundParseTypes";

function formatAmount(value: number | null | undefined, fractionDigits = 2): string {
    if (value === null || typeof value === "undefined" || Number.isNaN(value)) {
        return "—";
    }
    return value.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });
}

function formatRatio(value: number | null | undefined): string {
    if (value === null || typeof value === "undefined" || Number.isNaN(value)) {
        return "—";
    }
    return `${(value * 100).toFixed(1)}%`;
}

function CheckBadge({ ok, label }: { ok: boolean; label: string }): React.ReactElement {
    return (
        <span
            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${
                ok ? "bg-green-100 text-green-900" : "bg-red-100 text-red-900"
            }`}
        >
            {label}
        </span>
    );
}

function mappingStatusCell(row: FundParsePositionMappingRow): React.ReactElement {
    if (!row.matched) {
        return <span className="text-sm text-amber-700">No TPT match</span>;
    }
    return (
        <div className="flex flex-wrap gap-1">
            <CheckBadge ok={row.checks.scalingIdentityOk} label="Scaling" />
            <CheckBadge ok={row.checks.scalingAppliedOk} label="Applied" />
        </div>
    );
}

interface FundParsePositionMappingTableProps {
    summary: FundParsePositionMappingSummary | null;
}

export function FundParsePositionMappingTable({ summary }: FundParsePositionMappingTableProps): React.ReactElement {
    const columns = useMemo(
        () => [
            {
                header: "ISIN",
                accessorKey: "isin",
                size: 120
            },
            {
                header: "Fund",
                accessorKey: "fundName",
                size: 180
            },
            {
                header: "Share class",
                accessorKey: "shareclassName",
                size: 160
            },
            {
                header: "Client qty",
                accessorKey: "clientQuantity",
                cell: ({ row }) => formatAmount(row.original.clientQuantity, 4),
                size: 110
            },
            {
                header: "Client valuation",
                accessorKey: "clientValuation",
                cell: ({ row }) => formatAmount(row.original.clientValuation),
                size: 130
            },
            {
                header: "TPT NAV",
                accessorKey: "tptNav",
                cell: ({ row }) => formatAmount(row.original.tptNav),
                size: 120
            },
            {
                header: "CCY",
                accessorKey: "tptCurrency",
                size: 70
            },
            {
                header: "Scaling",
                accessorKey: "positionScaling",
                cell: ({ row }) => formatAmount(row.original.positionScaling, 6),
                size: 100
            },
            {
                header: "Coverage",
                accessorKey: "coverageRatio",
                cell: ({ row }) => formatRatio(row.original.coverageRatio),
                size: 90
            },
            {
                header: "Unscaled TPT MV",
                accessorKey: "unscaledTptMarketValueSum",
                cell: ({ row }) => formatAmount(row.original.unscaledTptMarketValueSum),
                size: 130
            },
            {
                header: "Scaled TPT MV",
                accessorKey: "scaledTptMarketValueSum",
                cell: ({ row }) => formatAmount(row.original.scaledTptMarketValueSum),
                size: 130
            },
            {
                header: "Checks",
                id: "checks",
                cell: ({ row }) => mappingStatusCell(row.original),
                size: 150
            }
        ],
        []
    );

    if (!summary) {
        return (
            <p className="text-sm text-gray-500">
                Run a create-report or read-report job to see consolidated client fund mappings against TPT lookthrough
                data.
            </p>
        );
    }

    if (!summary.rows.length) {
        return <p className="text-sm text-gray-500">No client fund positions were mapped to TPT lookthrough data.</p>;
    }

    return (
        <div>
            <p className="mb-3 text-sm text-gray-600">
                Consolidated client fund positions mapped to TPT lookthrough funds for valuation date{" "}
                <strong>{summary.valuationDate}</strong>. Coverage shows how much of the TPT fund NAV is represented in
                the uploaded lookthrough rows; it is not a same-day market reconciliation.
            </p>
            <ReactTable columns={columns} data={summary.rows} defaultHiddenColumns={["cic"]} initialPageSize={20} />
        </div>
    );
}
